import React, { useState } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import { Zap, Cloud, Bot } from 'lucide-react';

const SkillsSection = () => {
  const { skills, loading } = usePortfolio();
  const [activeCategory, setActiveCategory] = useState('All');

  const skillsData = skills || { technical: [], categories: ['All'] };
  
  const filteredSkills =
    activeCategory === 'All'
      ? skillsData.technical
      : skillsData.technical.filter((skill) => skill.category === activeCategory);

  return (
    <section id="skills" className="bg-[#f8fafc] py-24 md:py-32 relative">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#2563eb]/10 rounded-full blur-[150px]" />
      
      <div className="max-w-[87.5rem] mx-auto px-6 md:px-10 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#2563eb] text-sm uppercase tracking-widest font-semibold">What I work with</span>
          <h2 className="text-4xl md:text-6xl font-black text-[#1e3a5f] mt-2">
            Technical <span className="text-[#2563eb]">Skills</span>
          </h2>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {skillsData.categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-[#1e3a5f] text-white'
                  : 'bg-white text-[#64748b] border border-[#e2e8f0] hover:border-[#2563eb] hover:text-[#2563eb]'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Skills Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredSkills.map((skill, index) => (
            <div
              key={skill.name}
              className="group bg-white border border-[#e2e8f0] rounded-2xl p-6 hover:border-[#2563eb] hover:shadow-lg hover:shadow-[#2563eb]/10 transition-all duration-300 hover:scale-[1.02]"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex flex-col items-center text-center">
                <Zap className="w-8 h-8 text-[#2563eb] mb-3 group-hover:scale-110 transition-transform" />
                <h4 className="text-[#1e3a5f] font-semibold text-base">{skill.name}</h4>
                <span className="text-[#94a3b8] text-xs mt-1 uppercase tracking-wider">
                  {skill.category}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Skill Categories Summary */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <div className="bg-white border border-[#e2e8f0] rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
            <span className="text-[#2563eb] text-5xl font-black">AI</span>
            <h4 className="text-[#1e3a5f] font-bold text-lg mt-3">Machine Learning</h4>
            <p className="text-[#64748b] text-sm mt-2">
              Deep Learning, NLP, Computer Vision, Predictive Analytics
            </p>
          </div>
          <div className="bg-white border border-[#e2e8f0] rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
            <Cloud className="w-12 h-12 text-[#2563eb] mx-auto" />
            <h4 className="text-[#1e3a5f] font-bold text-lg mt-3">Cloud Platforms</h4>
            <p className="text-[#64748b] text-sm mt-2">
              Azure AI, AWS, Cloud Architecture, Deployment
            </p>
          </div>
          <div className="bg-white border border-[#e2e8f0] rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
            <Bot className="w-12 h-12 text-[#2563eb] mx-auto" />
            <h4 className="text-[#1e3a5f] font-bold text-lg mt-3">Conversational AI</h4>
            <p className="text-[#64748b] text-sm mt-2">
              Dialogflow, Rasa, MS Bot Framework, AI Agents
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;
